import * as RF from "@xyflow/react";
import "./styles.css";
import { H3 } from "../../../components/Heading";
import { CardLine } from "../../IndividualView";
import { Box } from "../../../components/Box";
import { Flex } from "../../../components/Flex";
import { Image } from "../../../components/Image";
import reject from "../../../assets/manual-reject.svg";
import success from "../../../assets/card-success-icon.svg";
import newIcon from "../../../assets/card-indeterminate-icon-gray.svg";
import inProgress from "../../../assets/card-indeterminate-icon.svg";
import inReview from "../../../assets/card-in-review-icon.svg";
import { FaEllipsisV } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { ROUTE } from "../../../routes";
import { SubjectTypeIcon } from "../../../components/SubjectTypeIcon";
import { useOrgChartMenu } from "./OrgChartMenuContext";
import { useAuth } from "../../../context/AuthContext";

export function CustomNode({ data, sourcePosition, targetPosition }: any) {
  const navigate = useNavigate();
  const nodeId = RF.useNodeId();
  const { openNodeId, toggleMenu, closeMenu, openAddRelationship } =
    useOrgChartMenu();
  const { hasPermission } = useAuth();
  const canCreateApplication = hasPermission("mandate.create");
  const canCreateScreening = hasPermission("screening.run");
  const canCreateMonitoring = hasPermission("monitoring.create");

  const isMenuOpen = nodeId !== null && openNodeId === nodeId;
  const profileId = String(data.id);

  // every destination goes through ROUTE.* — src/routes.ts is the source of
  // truth for paths
  const goTo = (path: string) => {
    closeMenu();
    navigate(path);
  };

  const viewProfile = () =>
    goTo(ROUTE.PROFILE_DETAIL_VIEW.replace(":id", profileId));

  const setAsFocus = () =>
    goTo(ROUTE.PROFILE_DETAIL_ORG.replace(":id", profileId));

  const createApplication = () =>
    goTo(ROUTE.PROFILE_APPLICATION_ENTITY_DETAILS.replace(":id", profileId));

  const createScreening = () =>
    goTo(ROUTE.PROFILE_SCREENING_SELECT_CHECKS.replace(":id", profileId));

  const createMonitoring = () =>
    goTo(ROUTE.PROFILE_MONITORING_DETAILS.replace(":id", profileId));

  const addRelationship = () => {
    closeMenu();
    openAddRelationship(data);
  };

  return (
    <Box className={`org-node ${data.is_focus ? "org-node-focus" : ""}`}>
      {/* handle sides follow the layout direction the chart set on this node,
          so horizontal orientations route from the left and right edges rather
          than out of the top and bottom of the card */}
      <RF.Handle type="target" position={targetPosition ?? RF.Position.Top} />

      {/* A negative offset tucks the menu up over the bottom of the card, so it
          reads as belonging to this node rather than floating beneath it. 14px
          lands on the card's own padding rather than covering the status row.
          NodeToolbar already renders one z-index above its node, so it paints
          on top. nodrag/nopan keep the canvas still while the menu is in use. */}
      <RF.NodeToolbar
        isVisible={isMenuOpen}
        position={RF.Position.Bottom}
        align="end"
        offset={-14}
      >
        <div
          className="node-menu-dropdown nodrag nopan"
          role="menu"
          aria-label={`Actions for ${data.label}`}
        >
          <button
            type="button"
            role="menuitem"
            className="node-menu-item"
            onClick={viewProfile}
          >
            View profile
          </button>
          <button
            type="button"
            role="menuitem"
            className="node-menu-item"
            onClick={setAsFocus}
          >
            Set as focus
          </button>
          <button
            type="button"
            role="menuitem"
            className="node-menu-item"
            onClick={addRelationship}
          >
            Add relationship
          </button>
          <button
            type="button"
            role="menuitem"
            className="node-menu-item"
            disabled
            title="Arrives with the relationship management panel"
          >
            Manage relationships
          </button>
          <div className="node-menu-divider" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="node-menu-item"
            onClick={createApplication}
            disabled={!canCreateApplication}
            title={
              canCreateApplication
                ? undefined
                : "You don't have permission to create applications"
            }
          >
            Create application
          </button>
          <button
            type="button"
            role="menuitem"
            className="node-menu-item"
            onClick={createScreening}
            disabled={!canCreateScreening}
            title={
              canCreateScreening
                ? undefined
                : "You don't have permission to run screenings"
            }
          >
            Create screening
          </button>
          <button
            type="button"
            role="menuitem"
            className="node-menu-item"
            onClick={createMonitoring}
            disabled={!canCreateMonitoring}
            title={
              canCreateMonitoring
                ? undefined
                : "You don't have permission to create monitoring records"
            }
          >
            Create monitoring
          </button>
        </div>
      </RF.NodeToolbar>

      <Flex gap="12">
        <Flex
          width={"20%"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <SubjectTypeIcon isEntity={data.icon === "entity"} size={36} />
        </Flex>
        <Flex
          width={"60%"}
          justifyContent={"space-between"}
          alignContent={"center"}
        >
          <H3>{data.label}</H3>
        </Flex>
        <Flex justifyContent="flex-end">
          <button
            type="button"
            className="node-menu-trigger nodrag nopan"
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            aria-label={`Actions for ${data.label}`}
            // nodrag/nopan stop the canvas reacting, but the click still
            // bubbles to React Flow's onNodeClick and would open the profile
            // pane, so it has to be stopped here as well
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              if (nodeId) toggleMenu(nodeId);
            }}
          >
            <FaEllipsisV />
          </button>
        </Flex>
      </Flex>
      {data.icon === "entity" && (
        <Flex gap="12">
          <Flex width={"20%"}></Flex>
          <Flex width={"60%"}>
            <CardLine>{data.registration_number}</CardLine>
          </Flex>
        </Flex>
      )}
      <Flex gap="12">
        <Flex width={"20%"}></Flex>
        <Flex width={"60%"}>
          <CardLine>{data.entity_type || "Individual"}</CardLine>
        </Flex>
      </Flex>
      <Flex gap="8" justifyContent="flex-end" alignItems={"center"}>
        {data?.profile_status === "new" && (
          <>
            <Image
              src={newIcon}
              alt="indicator icon"
              height="20px"
              width="24px"
            />
            <CardLine>{data?.profile_label}</CardLine>
          </>
        )}
        {data?.profile_status === "in progress" && (
          <>
            <Image
              src={inProgress}
              alt="indicator icon"
              height="20px"
              width="24px"
            />
            <CardLine>{data?.profile_label}</CardLine>
          </>
        )}
        {(data?.profile_status === "requires review" ||
          data?.profile_status === "secondary review" ||
          data?.profile_status === "periodic review") && (
          <>
            <Image
              src={inReview}
              alt="indicator icon"
              height="20px"
              width="24px"
            />
            <CardLine>{data?.profile_label}</CardLine>
          </>
        )}
        {data?.profile_status === "completed" && (
          <>
            <Image
              src={success}
              alt="indicator icon"
              height="20px"
              width="24px"
            />
            <CardLine>{data?.profile_label}</CardLine>
          </>
        )}
        {data?.profile_status === "rejected" && (
          <>
            <Image
              src={reject}
              alt="indicator icon"
              height="20px"
              width="24px"
            />
            <CardLine>{data?.profile_label}</CardLine>
          </>
        )}
      </Flex>
      <RF.Handle
        type="source"
        position={sourcePosition ?? RF.Position.Bottom}
      />
    </Box>
  );
}
